// hooks
import { useEffect, useState } from 'react'
import { EventLog, useServerStat } from 'api'
import _ from 'lodash'
import { endOfDay, startOfDay, sub } from 'date-fns'
import useStyles from './styles'
// components
import Typography from '@material-ui/core/Typography'
// import Divider from '@material-ui/core/Divider'
import {
  acceptablePercent,
  calulateMountpointUptimeInfo,
  optimalPercent,
  weakPercent,
  withoutConnectionPercent,
} from './Util'
import GridContainer from 'common/Grid/GridContainer'
import GridItem from 'common/Grid/GridItem'
import { Item } from './LinkUptimeBarChart'
import FormControl from '@material-ui/core/FormControl'
import FormGroup from '@material-ui/core/FormGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Checkbox from '@material-ui/core/Checkbox'
import { STATUSES } from './constants'
import { strings } from 'strings'
import Slider from '@material-ui/core/Slider'
import ButtonGroup from '@material-ui/core/ButtonGroup'
import { ToggleButton } from 'ToggleButton'
import Tooltip from '@material-ui/core/Tooltip'
import { formatDate } from 'format-helpers'
import { UptimePieChart } from './UptimePieChart'

type TimeRange = 'day' | 'week' | 'month'

const NewUptimeInfo = () => {
  const classes = useStyles()

  const { serverStat } = useServerStat()

  const [timeRange, setTimeRange] = useState<TimeRange>('day')
  const [negDays, setNegDays] = useState(0)

  const [startDate, setStartDate] = useState(() => {
    let date = new Date()
    date.setHours(0, 0, 0, 0)
    return date
  })
  const [endDate, setEndDate] = useState(new Date())

  const startDateSecs = startDate.getTime() / 1000
  const endDateSecs = endDate.getTime() / 1000

  const [statusFilters, setStatusFilters] = useState({
    optimal: true,
    acceptable: true,
    weak: true,
    veryWeak: true,
    withoutConnection: true,
  })

  useEffect(() => {
    const updatedEndDate =
      negDays === 0 ? new Date() : endOfDay(sub(new Date(), { days: negDays }))

    setEndDate(updatedEndDate)

    setStartDate(
      startOfDay(
        sub(updatedEndDate, {
          weeks: timeRange === 'week' ? 1 : 0,
          months: timeRange === 'month' ? 1 : 0,
        }),
      ),
    )
  }, [timeRange, negDays])

  if (!serverStat) return null

  const stabilityCounts = {
    optimal: 0,
    acceptable: 0,
    weak: 0,
    veryWeak: 0,
    withoutConnection: 0,
  }

  const entries = _.entries(serverStat.mountpoints)

  const items = entries.reduce<any[]>((acc, mountpoint) => {
    const allLogs = JSON.parse(mountpoint[1].eventLog) as EventLog[]

    // const filteredLogs = eventLogs.filter(
    //   (x) => x[0] > startDateSecs && x[0] < endDateSecs,
    // )

    const uptimeInfo = calulateMountpointUptimeInfo(
      allLogs,
      startDateSecs,
      endDateSecs,
      mountpoint[0],
    )

    const onlinePercent = uptimeInfo.onlinePercent

    if (onlinePercent > optimalPercent) {
      stabilityCounts.optimal++
    } else if (onlinePercent > acceptablePercent) {
      stabilityCounts.acceptable++
    } else if (onlinePercent > weakPercent) {
      stabilityCounts.weak++
    } else if (
      onlinePercent === withoutConnectionPercent &&
      uptimeInfo.allConnections.length === 0
    ) {
      stabilityCounts.withoutConnection++
    } else {
      stabilityCounts.veryWeak++
    }

    if (statusFilters[uptimeInfo.status])
      acc.push({
        ...uptimeInfo,
        mountpointId: mountpoint[1].id,
        mountpointName: mountpoint[0],
      })

    return acc
  }, [])

  return (
    <div className={classes.container}>
      <div className={classes.filtersBox}>
        <Typography variant="h6">نمودار قطعی وصلی ایستگاه ها</Typography>

        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography>تعداد کل ایستگاه ها: {entries.length}</Typography>
          <div
            style={{
              // width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <ButtonGroup disableElevation variant="outlined" size="small">
              <ToggleButton
                selected={timeRange === 'day'}
                onClick={() => setTimeRange('day')}
              >
                {strings.day}
              </ToggleButton>
              <ToggleButton
                selected={timeRange === 'week'}
                onClick={() => setTimeRange('week')}
              >
                {strings.week}
              </ToggleButton>
              <ToggleButton
                selected={timeRange === 'month'}
                onClick={() => setTimeRange('month')}
              >
                {strings.month}
              </ToggleButton>
            </ButtonGroup>
          </div>
        </div>

        {timeRange === 'day' && (
          <Slider
            value={negDays}
            onChange={(_, v) => setNegDays(v as number)}
            defaultValue={0}
            step={1}
            marks
            min={0}
            max={7}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => {
              if (v === 0) return strings.today
              if (v === 1) return strings.yesterday
              return v + strings.dayBefore
            }}
            ValueLabelComponent={({ open, value, children }) => (
              <Tooltip
                open={open}
                enterTouchDelay={0}
                placement="top"
                title={value}
              >
                {children}
              </Tooltip>
            )}
            style={{
              width: '97.5%',
            }}
          />
        )}

        <Typography
          style={{ width: '100%', textAlign: 'right', fontSize: '0.95rem' }}
        >{`${strings.from} ${formatDate(startDate)} | ${
          strings.to
        } ${formatDate(endDate)}`}</Typography>

        <UptimePieChart stabilityCounts={stabilityCounts} />

        <FormControl fullWidth>
          <FormGroup>
            {STATUSES.map(({ name, color, prefix }) => {
              const checked = statusFilters[name as keyof object]

              return (
                <FormControlLabel
                  label={
                    <div
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <div>
                        {`${strings[name as keyof object]}: %${_.round(
                          (stabilityCounts[name as keyof object] /
                            entries.length) *
                            100,
                          2,
                        )} (${stabilityCounts[name as keyof object]} ${
                          strings.mountpoint
                        })`}
                      </div>
                      <div style={{ fontSize: '0.75rem' }}>{prefix}</div>
                    </div>
                  }
                  name={name}
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={({ target }) => {
                        const { name, checked } = target
                        setStatusFilters({
                          ...statusFilters,
                          [name]: checked,
                        })
                      }}
                      style={{ color }}
                    />
                  }
                  className={classes.checkbox}
                  key={name}
                />
              )
            })}
          </FormGroup>
        </FormControl>
      </div>

      <div className={classes.barChartsContainer}>
        <GridContainer>
          {items.map((item) => (
            <GridItem sm={6} md={4} lg={6} xl={3} key={item.mountpointId}>
              <Item
                mountpointId={item.mountpointId}
                mountpointName={item.mountpointName}
                allConnections={item.allConnections}
                disconnectionsCount={item.disconnections.length}
                onlinePercent={item.onlinePercent}
                clickable
              />
            </GridItem>
          ))}
        </GridContainer>
      </div>
    </div>
  )
}

export default NewUptimeInfo
